/**
 * Driver — route list, load acceptance, and stops.
 *
 * The sequence is enforced, not suggested:
 *
 *   load sheet created (Admin) → driver ACCEPTS → route starts → stops → load-in
 *
 * Acceptance is the moment the driver becomes accountable for the residual
 * count at load-in, so the route cannot start without it. A mismatch can be
 * reported HERE, before the goods leave the godown — which is the only point at
 * which it can be resolved cheaply.
 */

import { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Page } from '../../components/Shell';
import {
  Banner, Button, Card, Chip, ConfirmSheet, Empty, ErrorState, Money, Sheet,
  Skeleton, TextArea, TextInput, useToast,
} from '../../components/ui';
import { useQuery, useRefreshOnFocus, usePoll, useOnline } from '../../lib/useApi';
import { useT } from '../../lib/i18n';
import { api, ApiError } from '../../lib/api';
import { dateShort, money, timeShort, piecesAndPacks } from '../../lib/format';
import { IconPhone, IconPin, IconChevron, IconTruck } from '../../components/Icons';

interface LoadSheetRow {
  id: string;
  load_sheet_number: string;
  load_date: string;
  trip_no: number;
  status: string;
  driver_accepted: number;
  accepted_at: string | null;
  route_started_at: string | null;
  route_closed_at: string | null;
  has_variance: number;
  variance_flagged: number;
  total_stops: number;
  stops_completed: number;
  registration_number: string | null;
  beat_name: string | null;
  godown_name: string | null;
  sku_count: number;
}

export default function Routes() {
  const t = useT();
  const navigate = useNavigate();

  const { data, loading, error, stale, refetch } = useQuery<{ items: LoadSheetRow[] }>(
    '/api/stock/load-sheets',
    { cacheKey: 'driver:loadsheets', ttlSeconds: 1800 },
  );
  useRefreshOnFocus(refetch);

  if (loading && !data) return <Page title={t('route.title')}><Skeleton rows={3} height={110} /></Page>;
  if (error && !data) {
    return <Page title={t('route.title')}><ErrorState message={error.message} onRetry={refetch} /></Page>;
  }

  const rows = data?.items ?? [];
  const active = rows.filter((r) => !['reconciled', 'cancelled'].includes(r.status));
  const past = rows.filter((r) => ['reconciled', 'cancelled'].includes(r.status));

  return (
    <Page title={t('route.title')}>
      <div className="stack">
        {stale && <Banner tone="warn">{t('app.staleData')}</Banner>}

        {active.length === 0 && past.length === 0 && <Empty title={t('route.noRoutes')} />}

        {active.map((r) => (
          <Card key={r.id} onClick={() => navigate(`/routes/${r.id}`)}>
            <div className="row-between" style={{ alignItems: 'flex-start' }}>
              <div className="grow" style={{ minWidth: 0 }}>
                <div className="row" style={{ gap: 8 }}>
                  <span className="t-semi">{r.load_sheet_number}</span>
                  <StatusChip row={r} />
                </div>
                <div className="t-xs t-muted" style={{ marginTop: 4 }}>
                  {dateShort(r.load_date)}
                  {r.trip_no > 1 && ` · trip ${r.trip_no}`}
                  {r.registration_number && ` · ${r.registration_number}`}
                  {r.beat_name && ` · ${r.beat_name}`}
                </div>
                <div className="row-wrap" style={{ gap: 6, marginTop: 8 }}>
                  <Chip outline>{r.sku_count} SKU</Chip>
                  <Chip outline>{t('route.stops', { n: r.total_stops })}</Chip>
                  {r.stops_completed > 0 && (
                    <Chip tone="ok">{r.stops_completed}/{r.total_stops} done</Chip>
                  )}
                </div>
              </div>
              <IconChevron width={20} height={20} style={{ color: 'var(--faint)' }} />
            </div>

            {/* Acceptance is the gate. Making it the primary action on the card
                keeps the driver from hunting for it. */}
            {r.driver_accepted === 0 && (
              <Banner tone="warn" title={t('route.acceptLoad')}>
                <span className="t-xs">{t('route.acceptLoadHint')}</span>
              </Banner>
            )}
          </Card>
        ))}

        {past.length > 0 && (
          <>
            <hr className="divider" />
            <span className="t-sm t-semi t-muted">{t('route.reconciled')}</span>
            {past.slice(0, 10).map((r) => (
              <Card key={r.id} onClick={() => navigate(`/routes/${r.id}`)}>
                <div className="row-between">
                  <div>
                    <div className="row" style={{ gap: 8 }}>
                      <span className="t-sm t-semi">{r.load_sheet_number}</span>
                      {r.variance_flagged === 1 && <Chip tone="danger">Flagged</Chip>}
                      {r.variance_flagged === 0 && r.has_variance === 1 && <Chip tone="warn">Variance</Chip>}
                    </div>
                    <div className="t-xs t-muted">{dateShort(r.load_date)}</div>
                  </div>
                  <IconChevron width={18} height={18} style={{ color: 'var(--faint)' }} />
                </div>
              </Card>
            ))}
          </>
        )}
      </div>
    </Page>
  );
}

function StatusChip({ row }: { row: LoadSheetRow }) {
  const t = useT();
  if (row.status === 'reconciled') return <Chip tone="ok">{t('route.reconciled')}</Chip>;
  if (row.status === 'in_transit') return <Chip tone="info">{t('route.inTransit')}</Chip>;
  if (row.driver_accepted === 1) return <Chip tone="ok">{t('route.accepted')}</Chip>;
  return <Chip tone="warn">{t('route.acceptLoad')}</Chip>;
}

// ---------------------------------------------------------------------------
// Route detail
// ---------------------------------------------------------------------------

interface VanStock {
  loadSheetNumber: string;
  status: string;
  items: {
    productId: string; skuCode: string; productName: string;
    loaded: number; delivered: number; extraBoxSold: number; returnedIn: number;
    expectedResidual: number; actualResidual: number | null; variance: number | null;
    /**
     * The van-stock endpoint has always returned these; this screen's own
     * type dropped them, so every quantity below rendered as bare pieces.
     * A driver counting 288 of something off a van floor counts boxes.
     */
    packUom: string | null; packQty: number | null;
  }[];
}

interface Stop {
  dispatch_id: string;
  stop_sequence: number;
  status: string;
  invoice_number: string;
  net_amount_paise: number;
  shop_name: string;
  address_line1: string;
  landmark: string | null;
  phone: string | null;
  latitude: number | null;
  longitude: number | null;
  customer_name: string;
  mapsUrl: string | null;
  dialUrl: string | null;
}

export function RouteDetail() {
  const t = useT();
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const toast = useToast();
  const online = useOnline();

  const vanStock = useQuery<VanStock>(id ? `/api/stock/load-sheets/${id}/van-stock` : null, {
    cacheKey: `van:${id}`, ttlSeconds: 1800,
  });
  const sheets = useQuery<{ items: LoadSheetRow[] }>('/api/stock/load-sheets', {
    cacheKey: 'driver:loadsheets', ttlSeconds: 1800,
  });
  useRefreshOnFocus(() => {
    void vanStock.refetch();
    void sheets.refetch();
    void stopsQuery.refetch();
  });

  const sheet = sheets.data?.items.find((s) => s.id === id) ?? null;


  const [acceptOpen, setAcceptOpen] = useState(false);
  const [mismatch, setMismatch] = useState(false);
  const [mismatchNote, setMismatchNote] = useState('');
  const [odometer, setOdometer] = useState('');
  const [busy, setBusy] = useState(false);
  /**
   * THE STOPS COME FROM THE SERVER, NOT FROM THE START BUTTON.
   *
   * They used to live only in React state, set from the response to "Start
   * route". Closing the app lost them, and pressing Start again returns "Route
   * already started" — so a driver who locked his phone had no way back to his
   * round. A phone locks, a call arrives, Android reclaims memory mid-delivery;
   * "do not close the app" is not a delivery process.
   *
   * Cached like the other driver queries, because this screen is used in places
   * with no signal. `stale` from the cache is far better than nothing when
   * somebody is standing outside a shop trying to remember which one is next.
   */
  const stopsQuery = useQuery<{ stops: Stop[] }>(
    id ? `/api/stock/load-sheets/${id}/stops` : null,
    { cacheKey: `stops:${id}`, ttlSeconds: 1800 },
  );
  const stops = stopsQuery.data?.stops ?? null;
  /**
   * While the van is out, watch for a stop the office adds.
   *
   * Requested 3 Oct: "driver is in way and admin get order so he assign
   * immediate to the driver and driver can able to see it in app". The server
   * now notifies and pushes, but a push can be declined, muted, or simply not
   * granted on the phone — and the driver is already looking at the screen
   * the new stop belongs on. Thirty seconds is the gap between the office
   * pressing Add and the drop appearing in his list.
   *
   * ONLY while in transit: before the route starts nothing can be added that
   * the accept screen will not already show, and after it closes the sheet is
   * settled. A poll that runs on a finished route is requests nobody reads,
   * on the driver's data.
   */
  usePoll(() => { void stopsQuery.refetch(); }, 30_000, sheet?.status === 'in_transit');

  async function accept() {
    setBusy(true);
    try {
      await api.post(`/api/stock/load-sheets/${id}/accept`, {
        openingOdometer: odometer ? Number(odometer) : undefined,
        reportMismatch: mismatch || undefined,
        mismatchNote: mismatch ? mismatchNote.trim() : undefined,
      });
      toast.show(t('route.accepted'), 'ok');
      setAcceptOpen(false);
      void sheets.refetch();
    } catch (err) {
      toast.show(err instanceof ApiError ? err.message : t('app.somethingWrong'), 'danger');
    } finally {
      setBusy(false);
    }
  }

  async function startRoute() {
    setBusy(true);
    try {
      const result = await api.post<{ routeStarted: boolean; stops: Stop[] }>(
        `/api/stock/load-sheets/${id}/start-route`,
      );
      // The POST already returns them, so the screen updates without waiting
      // for a round trip — but the query above is what makes them survive the
      // app being closed.
      void result.stops;
      void stopsQuery.refetch();
      toast.show(t('route.startRoute'), 'ok');
      void sheets.refetch();
    } catch (err) {
      if (err instanceof ApiError && err.code === 'LOAD_NOT_ACCEPTED') {
        toast.show(t('route.acceptLoadHint'), 'danger');
        setAcceptOpen(true);
      } else {
        toast.show(err instanceof ApiError ? err.message : t('app.somethingWrong'), 'danger');
      }
    } finally {
      setBusy(false);
    }
  }

  if (vanStock.loading && !vanStock.data) {
    return <Page title={t('route.loadSheet')} back><Skeleton rows={4} /></Page>;
  }
  if (!vanStock.data) return <Page title={t('route.loadSheet')} back><Empty /></Page>;

  const v = vanStock.data;
  const accepted = sheet?.driver_accepted === 1;
  const inTransit = sheet?.status === 'in_transit';
  const reconciled = sheet?.status === 'reconciled';

  return (
    <Page title={v.loadSheetNumber} subtitle={t(`route.${inTransit ? 'inTransit' : accepted ? 'accepted' : 'acceptLoad'}`)} back="/routes">
      <div className="stack">
        {!accepted && (
          <Banner tone="warn" title={t('route.acceptLoad')}>
            <span className="t-sm">{t('route.acceptLoadHint')}</span>
          </Banner>
        )}
        {sheet?.variance_flagged === 1 && (
          <Banner tone="danger" title="Variance flagged">
            <span className="t-sm">An unexplained difference was recorded at load-in.</span>
          </Banner>
        )}

        <Card flush>
          <div className="card__header">
            <h3>{t('route.vanStock')}</h3>
            <Chip outline>{v.items.length} SKU</Chip>
          </div>
          <div className="list">
            {v.items.map((item) => (
              <div key={item.productId} className="list__item list__item--static">
                <div className="grow" style={{ minWidth: 0 }}>
                  <div className="t-sm t-semi t-clip">{item.productName}</div>
                  <div className="t-xs t-muted">{item.skuCode}</div>
                  <div className="row-wrap t-xs" style={{ gap: 8, marginTop: 4 }}>
                    <span className="t-muted">
                      {t('route.loaded')} <strong>{item.loaded}</strong>
                      {item.packQty && item.packQty > 1
                        && ` (${piecesAndPacks(item.loaded, {
                          baseUom: 'PCS', packUom: item.packUom, packQty: item.packQty,
                        }).split(' · ')[1]})`}
                    </span>
                    {item.delivered > 0 && (
                      <span className="t-muted">{t('route.delivered')} <strong>{item.delivered}</strong></span>
                    )}
                    {item.extraBoxSold > 0 && (
                      <span className="t-muted">{t('route.extraSold')} <strong>{item.extraBoxSold}</strong></span>
                    )}
                    {item.returnedIn > 0 && (
                      <span className="t-muted">returned <strong>{item.returnedIn}</strong></span>
                    )}
                  </div>
                </div>
                <div className="t-right">
                  <div className="t-lg t-bold t-mono">{item.expectedResidual}</div>
                  <div className="t-xs t-muted">{t('route.expected')}</div>
                  {/*
                    The same number in boxes. This is the figure the driver
                    checks against what is physically left on the van, and
                    nobody counts 288 loose bottles when they are stacked in
                    twelves.
                  */}
                  {item.packQty && item.packQty > 1 && (
                    <div className="t-xs t-muted">
                      {piecesAndPacks(item.expectedResidual, {
                        baseUom: 'PCS', packUom: item.packUom, packQty: item.packQty,
                      }).split(' · ')[1]}
                    </div>
                  )}
                  {item.variance !== null && item.variance !== 0 && (
                    <div className={`t-xs ${item.variance < 0 ? 't-danger' : 't-warn'}`}>
                      {item.variance > 0 ? '+' : ''}{item.variance}
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        </Card>

        {(stops ?? []).length > 0 && (
          <Card flush>
            <div className="card__header"><h3>{t('route.stops', { n: (stops ?? []).length })}</h3></div>
            <div className="list">
              {(stops ?? []).map((s) => (
                <div key={s.dispatch_id} className="list__item list__item--static" style={{ flexWrap: 'wrap' }}>
                  <span
                    className="avatar"
                    style={{ width: 26, height: 26, flex: '0 0 26px', fontSize: 12 }}
                  >{s.stop_sequence}</span>
                  <div className="grow" style={{ minWidth: 0 }}>
                    {/* Shop name wraps rather than clipping — the driver must read
                        the whole stop name. */}
                    <div className="t-sm t-semi">{s.shop_name}</div>
                    <div className="t-xs t-muted t-clip">
                      {s.invoice_number} · {s.address_line1}
                    </div>
                  </div>
                  <div className="row" style={{ gap: 6, width: '100%', marginTop: 8 }}>
                    {s.dialUrl && (
                      <a className="btn btn--ghost btn--sm" href={s.dialUrl}>
                        <IconPhone width={14} height={14} />
                      </a>
                    )}
                    {s.mapsUrl && (
                      <a className="btn btn--ghost btn--sm" href={s.mapsUrl} target="_blank" rel="noreferrer">
                        <IconPin width={14} height={14} />
                      </a>
                    )}
                    <Button
                      size="sm" className="grow"
                      disabled={s.status === 'delivered'}
                      onClick={() => navigate(`/pod/${s.dispatch_id}`, {
                        state: {
                          shopName: s.shop_name, invoiceNumber: s.invoice_number,
                          loadSheetId: id, customerName: s.customer_name,
                        },
                      })}
                    >
                      {s.status === 'delivered' ? t('app.done') : t('pod.title')}
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          </Card>
        )}

        {/*
          A route in transit with no stops on screen.

          This used to say "Tap Start route to load your stop list" — advice
          that could not work, because the route was already started and that
          button now refuses with "Route already started". The driver was told
          to do the one thing guaranteed to fail.

          Now the stops come from the server, so this only appears while they
          are loading or if the fetch failed, and it says which.
        */}
        {inTransit && !stops && (
          stopsQuery.loading
            ? <Skeleton rows={3} />
            : (
              <Banner tone="warn" title="Could not load your stops">
                <div className="stack-1" style={{ gap: 6 }}>
                  <span className="t-sm">
                    {stopsQuery.error?.message
                      ?? 'No stops came back for this route. Check with the office before '
                       + 'starting deliveries.'}
                  </span>
                  <Button size="sm" variant="ghost" onClick={() => void stopsQuery.refetch()}>
                    {t('app.retry')}
                  </Button>
                </div>
              </Banner>
            )
        )}
      </div>

      {!reconciled && (
        <div className="actionbar">
          {!accepted && (
            <Button block size="lg" onClick={() => setAcceptOpen(true)}>{t('route.acceptLoad')}</Button>
          )}
          {accepted && !inTransit && (
            <Button block size="lg" busy={busy} onClick={() => void startRoute()}>
              {t('route.startRoute')}
            </Button>
          )}
          {inTransit && (
            <>
              {!stops && (
                <Button variant="ghost" block busy={busy} onClick={() => void startRoute()}>
                  {t('route.stops', { n: sheet?.total_stops ?? 0 })}
                </Button>
              )}
              {/* The entry point that did not exist. `pod.extraBoxSale` was
                  translated into both languages and referenced by nothing, so
                  a driver could not record a doorstep sale — and the load-in
                  variance blamed him for it. */}
              <Button variant="ghost" block onClick={() => navigate(`/routes/${id}/extra-box`)}>
                {t('pod.extraBoxSale')}
              </Button>
              <Button block onClick={() => navigate(`/routes/${id}/load-in`)}>
                {t('route.loadIn')}
              </Button>
            </>
          )}
        </div>
      )}

      <Sheet
        open={acceptOpen}
        onClose={() => setAcceptOpen(false)}
        title={t('route.acceptLoad')}
        footer={
          <>
            <Button variant="ghost" block onClick={() => setAcceptOpen(false)} disabled={busy}>
              {t('app.cancel')}
            </Button>
            <Button block busy={busy} onClick={() => void accept()}>{t('app.confirm')}</Button>
          </>
        }
      >
        <div className="stack-4">
          <Banner tone="warn">
            <span className="t-sm">
              {t('route.acceptLoadHint')} You are responsible for the residual count
              when you return.
            </span>
          </Banner>

          <Card flush>
            <div className="list">
              {v.items.map((item) => (
                <div key={item.productId} className="list__item list__item--static">
                  <div className="grow" style={{ minWidth: 0 }}>
                    <div className="t-sm t-clip">{item.productName}</div>
                    {/*
                      Boxes here above anywhere else on this screen. This is
                      the moment the driver takes responsibility for the
                      count, and he is checking it against a stack of cartons
                      on a van floor — not against a number of loose pieces he
                      has no way to verify.
                    */}
                    {item.packQty && item.packQty > 1 && (
                      <div className="t-xs t-muted">
                        {piecesAndPacks(item.loaded, {
                          baseUom: 'PCS', packUom: item.packUom, packQty: item.packQty,
                        }).split(' · ')[1]}
                      </div>
                    )}
                  </div>
                  <span className="t-lg t-bold t-mono">{item.loaded}</span>
                </div>
              ))}
            </div>
          </Card>

          <TextInput
            label={t('route.odometer')}
            numeric
            value={odometer}
            onChange={(e) => setOdometer(e.target.value.replace(/\D/g, ''))}
          />

          {/* Reporting a mismatch at acceptance is far cheaper than discovering
              it at load-in, when the goods have already been on the road. */}
          <div className="stack-2">
            <button
              className="btn btn--ghost btn--block"
              onClick={() => setMismatch(!mismatch)}
              aria-pressed={mismatch}
            >{t('route.reportMismatch')}</button>
            {mismatch && (
              <TextArea
                label={t('route.mismatchNote')}
                required
                value={mismatchNote}
                onChange={(e) => setMismatchNote(e.target.value)}
                maxLength={1000}
              />
            )}
          </div>
        </div>
      </Sheet>
    </Page>
  );
}
